import isBefore from 'date-fns/isBefore'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'
import { Formik, getIn, useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { SelectTags } from 'components/v2/SelectTags'
import { Label } from 'components/v2/Label'
import { StyledLink } from 'components/v2/Link'
import Heading from 'components/v2/Heading'
import { CloseIcon } from '@10xLabs/react-web/components/Icons/Close'
import { Checkbox } from 'components/v2/Checkbox'
import { InputCurrency } from 'components/InputCurrency'
import { InputDate } from 'components/v2/InputDate'
import { InputDateTime } from 'components/InputDateTime'
import { InputNumber } from 'components/InputNumber'
import { InputPercentage } from 'components/InputPercentage'
import { Radio } from 'components/v2/Radio'
import FormLayout from 'features/App/components/FormLayout'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { CampaignAutocomplete } from 'features/Campaigns/components/CampaignAutocomplete'
import { CHANNEL } from 'features/Coupons/components/Channel/constants'
import { channelMessages } from 'features/Coupons/components/Channel/messages'
import { DISCOUNT_TYPE } from 'features/Coupons/components/DiscountType/constants'
import { USER_LIMIT } from 'features/Coupons/components/UserLimit/constants'
import { userLimitMessages } from 'features/Coupons/components/UserLimit/messages'
import CouponCitySegments from 'features/Coupons/components/CouponCitySegments'
import { CONFIRMATION_MODALITIES } from 'features/Services/components/ConfirmationModalities/constants'
import { confirmationModalitiesMessages } from 'features/Services/components/ConfirmationModalities/messages'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { getUnprocessableErrors } from 'utils/error'
import { sanitizePrice } from 'utils/math'
import { device } from 'utils/style'
import { GTBookingType, GTCampaignArea, GTDiscountType } from 'gql/types.generated'
import SelectPassengerTypes from 'components/SelectPassengerTypes'
import { safeFormatMessage } from 'utils/intl'
import { canSelectCompany, getDefaultCompanyForForm } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'
import { INewCouponForm, INewCouponFormProps, INewCouponProps } from './types'
import { schema } from './schema'
import { isEmpty } from 'lodash'

const PassengerTypesLabel = styled(Label)`
  margin-bottom: 8px;
`
const IntervalDelete = styled.div`
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translate(100%, -50%);
`

const NestedRow = styled(Row)`
  display: flex;
  flex-direction: column;

  @media ${device.laptop} {
    flex-direction: row;
  }
`

const DepartureDatesTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const EndsAtColumn = styled(Column)`
  align-self: flex-end;
  position: relative;
`

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
`

const EmptyDepartureDates = styled.div`
  text-align: center;
  font-size: 15px;
`

const CheckboxWrapper = styled.div`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`

const CHANNELS_LIST = [CHANNEL.SEM, CHANNEL.FACEBOOK]

const messages = defineMessages({
  bookingTypePlaceholder: {
    id: 'coupons.form.bookingTypes.placeholder',
    defaultMessage: 'Select booking type'
  },
  companyPlaceholder: {
    id: 'companies.autocomplete.placeholder',
    defaultMessage: 'Select company'
  },
  campaignPlaceholder: {
    id: 'campaigns.autocomplete.placeholder',
    defaultMessage: 'Select campaign'
  },
  codePlaceholder: {
    id: 'dictionary.code',
    defaultMessage: 'Code'
  },
  couponTypePlaceholder: {
    id: 'coupons.form.type.placeholder',
    defaultMessage: 'Select type'
  },
  segmentsPlaceholder: {
    id: 'coupons.form.segments.placeholder',
    defaultMessage: 'Select segments'
  },
  channelsPlaceholder: {
    id: 'coupons.form.channels.placeholder',
    defaultMessage: 'Select channel'
  },
  salesChannelsPlaceholder: {
    id: 'coupons.form.salesChannels.placeholder',
    defaultMessage: 'Select sales channel'
  },
  startsAtPlaceholder: {
    id: 'coupons.form.startsAt.placeholder',
    defaultMessage: 'Select starting date'
  },
  endsAtPlaceholder: {
    id: 'coupons.form.endsAt.placeholder',
    defaultMessage: 'Select ending date'
  },
  onlyRegisteredUsersCheckbox: {
    id: 'coupons.form.onlyRegisteredUsers.label',
    defaultMessage: 'Only registered users'
  }
})

const bookingTypeMessages = defineMessages({
  [GTBookingType.OneWay]: {
    id: 'coupons.form.bookingTypes.oneWay',
    defaultMessage: 'One way'
  },
  [GTBookingType.RoundTrip]: {
    id: 'coupons.form.bookingTypes.roundTrip',
    defaultMessage: 'Round trip'
  }
})
const CONFIRMATION_MODALITIES_LIST = [CONFIRMATION_MODALITIES.PAID, CONFIRMATION_MODALITIES.RESERVED]

const getRandomCode = (): TSFixMe => {
  const randomStr = Math.random().toString(36)
  return randomStr.substring(randomStr.length - 6).toUpperCase()
}

const getFilteredChannels = (values: TSFixMe): TSFixMe => {
  return CHANNELS_LIST.filter((channel): TSFixMe => {
    return values.indexOf(channel) === -1
  })
}
const BOOKING_TYPE_OPTIONS = Object.values(GTBookingType)

const NewCouponForm = ({ error, loading, isEdit, campaign }: INewCouponFormProps): ReactElement => {
  const { values, touched, errors, setFieldValue, setValues, setFieldTouched, handleBlur, handleSubmit, dirty } =
    useFormikContext<INewCouponForm>()
  const intl = useIntl()
  const login = useLogin()

  const handleCompanyChange = (value: TSFixMe): TSFixMe => {
    setValues({
      ...values,
      company: value,
      // @ts-expect-error ts(2470)
      campaign: {
        id: '',
        name: ''
      },
      constraints: {
        ...values.constraints,
        segments: []
      }
    })
  }

  const handleCampaignChange = (value: TSFixMe): TSFixMe => {
    setFieldValue('campaign', value)
    setFieldTouched('campaign', true)
    setFieldValue('startsAt', new Date(value.startsAt))
    setFieldValue('endsAt', new Date(value.endsAt))
  }

  const handleChannelsChange = (channels: TSFixMe): TSFixMe => {
    setFieldValue('channels', channels)
  }

  const handleSalesChannelsChange = (salesChannels: TSFixMe): TSFixMe => {
    setFieldValue('constraints.salesChannels', salesChannels)
  }

  const handleDiscountTypeChange = ({ target: { id } }: TSFixMe): TSFixMe => {
    setFieldValue('discountType', id)
  }

  const handleUserLimitChange = ({ target: { id } }: TSFixMe): TSFixMe => {
    setFieldValue('constraints.userLimit', id)
  }

  const handleConfirmationModalitiesChange = ({ target: { id } }: TSFixMe): TSFixMe => {
    let confirmationModalities = values.constraints.confirmationModalities

    if (confirmationModalities.indexOf(id) === -1) {
      confirmationModalities = [...confirmationModalities, id]
    } else {
      confirmationModalities = confirmationModalities.filter((value: TSFixMe): TSFixMe => value !== id)
    }

    setFieldValue('constraints.confirmationModalities', confirmationModalities)
  }

  const handleOnlyRegisteredUsersChange = ({ target: { checked } }: TSFixMe): TSFixMe => {
    setFieldValue('constraints.onlyRegisteredUsers', checked)
  }

  const addInterval = (): TSFixMe => {
    setFieldValue('constraints.departureDateRanges', [
      ...values.constraints.departureDateRanges,
      {
        from: '',
        to: ''
      }
    ])
  }

  const deleteInterval = (index: TSFixMe): TSFixMe => {
    setFieldValue(
      'constraints.departureDateRanges',
      values.constraints.departureDateRanges.filter(
        (_: TSFixMe, intervalIndex: TSFixMe): TSFixMe => intervalIndex !== index
      )
    )
  }

  const isPercentage = values.discountType === DISCOUNT_TYPE.PERCENTAGE

  const bookingTypeOptions = BOOKING_TYPE_OPTIONS.filter((value) => {
    return !values.constraints.bookingTypes?.includes(value)
  })
  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        {canSelectCompany(login.accessToken) && (
          <Row>
            <Column width="50%">
              <CompanyAutocomplete
                id="company.id"
                onChange={handleCompanyChange}
                onBlur={handleBlur}
                value={values.company.name}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                disabled={isEdit || !isEmpty(campaign)}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column width="50%">
            <Input
              id="code"
              onChange={(e: React.ChangeEvent<HTMLInputElement>): TSFixMe => {
                setFieldValue('code', e.target.value.trim().toUpperCase())
              }}
              onBlur={handleBlur}
              value={values.code}
              placeholder={intl.formatMessage(messages.codePlaceholder)}
              touched={touched.code}
              errors={errors.code}
              label={<FormattedMessage id="dictionary.code" defaultMessage="Code" />}
              readOnly={isEdit}
            />
          </Column>

          <Column width="50%">
            <Label>
              <FormattedMessage id="dictionary.campaign" defaultMessage="Campaign" />
            </Label>

            {canSelectCompany(login.accessToken) && !values.company.id && (
              <FormattedMessage id="coupons.form.company.empty" defaultMessage="Select company first" />
            )}

            {values.company.id && !(campaign && campaign.id) && (
              <CampaignAutocomplete
                id="campaign.id"
                onChange={handleCampaignChange}
                onBlur={handleBlur}
                value={values.campaign.name}
                placeholder={intl.formatMessage(messages.campaignPlaceholder)}
                touched={touched.campaign && touched.campaign.id}
                errors={errors.campaign && errors.campaign.id}
                companyId={values.company.id}
                filter={(campaign: TSFixMe): TSFixMe => {
                  return isBefore(new Date(), new Date(campaign.endsAt))
                }}
                areas={[GTCampaignArea.Marketing, GTCampaignArea.CustomerSupport]}
              />
            )}

            {campaign && campaign.id && <Input value={campaign.name} readOnly />}
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputDateTime
              id="startsAt"
              intl={intl}
              /*@ts-expect-error ts(2322) */
              value={values.startsAt}
              placeholder={intl.formatMessage(messages.startsAtPlaceholder)}
              onChange={(value): TSFixMe => {
                setFieldValue('startsAt', value)
                /*@ts-expect-error ts(2322) */
                if (!values.endsAt || isBefore(values.endsAt, value)) {
                  /*@ts-expect-error ts(2322) */
                  setFieldValue('endsAt', endOfDay(value))
                }
              }}
              /*@ts-expect-error ts(2322) */
              disabledDays={{
                before: values.campaign.id ? new Date(values.campaign.startsAt) : new Date(),
                after: values.campaign.id && new Date(values.campaign.endsAt)
              }}
              touched={touched.startsAt}
              errors={errors.startsAt}
              label={<FormattedMessage id="coupons.form.startsAt.label" defaultMessage="Starting date" />}
            />
          </Column>

          <Column width="50%">
            <InputDateTime
              id="endsAt"
              intl={intl}
              /*@ts-expect-error ts(2322) */
              value={values.endsAt}
              placeholder={intl.formatMessage(messages.endsAtPlaceholder)}
              onChange={(value: TSFixMe): TSFixMe => {
                setFieldValue('endsAt', value)

                if (!values.startsAt || isBefore(value, values.startsAt)) {
                  setFieldValue('startsAt', startOfDay(value))
                }
              }}
              /*@ts-expect-error ts(2322) */
              disabledDays={{
                before: values.campaign.id ? new Date(values.campaign.startsAt) : new Date(),
                after: values.campaign.id && new Date(values.campaign.endsAt)
              }}
              touched={touched.endsAt}
              errors={errors.endsAt}
              label={<FormattedMessage id="coupons.form.endsAt.label" defaultMessage="Ending date" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectTags
              id="channels"
              values={values.channels}
              placeholder={intl.formatMessage(messages.channelsPlaceholder)}
              items={getFilteredChannels(values.channels)}
              getTagLabel={(channel: TSFixMe): TSFixMe => {
                return intl.formatMessage(channelMessages[channel])
              }}
              onChange={handleChannelsChange}
              touched={touched.channels}
              errors={errors.channels}
              label={<FormattedMessage id="dictionary.channels" defaultMessage="Channels" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectSalesChannelsV2
              id="constraints.salesChannels"
              values={values.constraints.salesChannels}
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              onChange={handleSalesChannelsChange}
              touched={getIn(touched, 'constraints.salesChannels')}
              errors={getIn(errors, 'constraints.salesChannels')}
              label={<FormattedMessage id="dictionary.salesChannels" defaultMessage="Sales channels" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="coupons.form.discountType.label" defaultMessage="Discount type" />
            </Label>

            <Radio
              id={DISCOUNT_TYPE.PERCENTAGE}
              name="discountType"
              checked={values.discountType === DISCOUNT_TYPE.PERCENTAGE}
              onChange={handleDiscountTypeChange}
            >
              %
            </Radio>

            <Radio
              id={DISCOUNT_TYPE.AMOUNT}
              name="discountType"
              checked={values.discountType === DISCOUNT_TYPE.AMOUNT}
              onChange={handleDiscountTypeChange}
            >
              $
            </Radio>
          </Column>

          <Column>
            <label>
              <Label>
                <FormattedMessage id="dictionary.value" defaultMessage="Value" />
              </Label>

              {isPercentage ? (
                <InputPercentage
                  id="value"
                  value={values.value}
                  placeholder="0%"
                  onChange={(value: TSFixMe): TSFixMe => {
                    setFieldValue('value', value)
                  }}
                  onBlur={handleBlur}
                  touched={touched.value}
                  errors={errors.value}
                />
              ) : (
                <InputCurrency
                  id="value"
                  value={values.value}
                  placeholder="$0.00"
                  onChange={(value): TSFixMe => {
                    setFieldValue('value', value)
                  }}
                  onBlur={handleBlur}
                  touched={touched.value}
                  errors={errors.value}
                  hideSuffix
                />
              )}
            </label>
          </Column>
        </Row>

        <CouponCitySegments />

        <Row>
          <Column width="50%">
            <PassengerTypesLabel>
              <FormattedMessage
                id="coupons.form.constraints.passengerTypes"
                defaultMessage="Passenger Types (optional)"
              />
            </PassengerTypesLabel>
            <SelectPassengerTypes
              id="passengerTypes"
              salesChannels={values.constraints.salesChannels}
              onChange={(value): void => {
                setFieldValue('passengerTypes', value)
              }}
              showCountryCode
              values={values.passengerTypes ?? []}
              touched={Boolean(touched.passengerTypes)}
              errors={errors.passengerTypes as string}
            />
          </Column>
          <Column width="50%">
            <SelectTags
              id="constraints.bookingTypes"
              values={values.constraints.bookingTypes as string[]}
              placeholder={intl.formatMessage(messages.bookingTypePlaceholder)}
              items={bookingTypeOptions}
              getTagLabel={(bookingType: TSFixMe): string => {
                return safeFormatMessage({ intl, messages: bookingTypeMessages, key: bookingType })
              }}
              onChange={(value: TSFixMe): void => {
                setFieldValue('constraints.bookingTypes', value)
              }}
              touched={touched.constraints && touched.constraints.bookingTypes}
              errors={errors.constraints && errors.constraints.bookingTypes}
              label={<FormattedMessage id="coupons.form.bookingTypes" defaultMessage="Booking type" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputNumber
              id="constraints.minTicketsCount"
              value={values.constraints.minTicketsCount}
              placeholder="0"
              onChange={(value: string): void => {
                setFieldValue('constraints.minTicketsCount', value)
              }}
              onBlur={handleBlur}
              touched={touched.constraints && touched.constraints.minTicketsCount}
              maskOptions={{
                suffix: '',
                prefix: '',
                allowDecimal: false
              }}
              errors={errors.constraints && errors.constraints.minTicketsCount}
              label={<FormattedMessage id="coupons.form.minTicketsCount" defaultMessage="Minimum tickets" />}
            />
          </Column>
          <Column width="50%">
            <InputCurrency
              id="constraints.minSale"
              value={values.constraints.minSale}
              placeholder="$0.00"
              onChange={(value): TSFixMe => {
                setFieldValue('constraints.minSale', Number(value))
              }}
              label={
                <FormattedMessage
                  id="coupons.form.minSale"
                  defaultMessage="Minimum sale <small>(Optional)</small>"
                  values={{
                    small: (msg: TSFixMe): TSFixMe => <small>{msg}</small>
                  }}
                />
              }
              hideSuffix
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputNumber
              id="constraints.limit"
              value={values.constraints.limit}
              placeholder="0"
              onChange={(value: TSFixMe): TSFixMe => {
                setFieldValue('constraints.limit', value)
              }}
              onBlur={handleBlur}
              touched={touched.constraints && touched.constraints.limit}
              errors={errors.constraints && errors.constraints.limit}
              maskOptions={{
                suffix: '',
                prefix: '',
                allowDecimal: false
              }}
              label={<FormattedMessage id="coupons.form.limit" defaultMessage="Quantity (Limit)" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="coupons.form.userLimit.label" defaultMessage="Uses per user" />
            </Label>

            <Radio
              id={USER_LIMIT.ONE_TIME}
              name="constraints.userLimit"
              checked={values.constraints.userLimit === USER_LIMIT.ONE_TIME}
              onChange={handleUserLimitChange}
            >
              {intl.formatMessage(userLimitMessages[USER_LIMIT.ONE_TIME])}
            </Radio>

            <Radio
              id={USER_LIMIT.MULTIPLE}
              name="uconstraints.serLimit"
              checked={values.constraints.userLimit === USER_LIMIT.MULTIPLE}
              onChange={handleUserLimitChange}
            >
              {intl.formatMessage(userLimitMessages[USER_LIMIT.MULTIPLE])}
            </Radio>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="coupons.form.onlyRegisteredUsers.label" defaultMessage="Registered users" />
            </Label>

            <Checkbox
              id="onlyRegisteredUsers"
              checked={values.constraints.onlyRegisteredUsers}
              onChange={handleOnlyRegisteredUsersChange}
            >
              {intl.formatMessage(messages.onlyRegisteredUsersCheckbox)}
            </Checkbox>
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage
                id="coupons.form.confirmationModalities.label"
                defaultMessage="Confirmation modalities"
              />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <div>
              {CONFIRMATION_MODALITIES_LIST.map((confirmationModality: TSFixMe): TSFixMe => {
                return (
                  <CheckboxWrapper key={confirmationModality}>
                    <Checkbox
                      id={confirmationModality}
                      checked={values.constraints.confirmationModalities.indexOf(confirmationModality) > -1}
                      onChange={handleConfirmationModalitiesChange}
                    >
                      {/*@ts-expect-error ts(2322) */}
                      {intl.formatMessage(confirmationModalitiesMessages[confirmationModality])}
                    </Checkbox>
                  </CheckboxWrapper>
                )
              })}

              {errors.constraints?.confirmationModalities && touched.constraints?.confirmationModalities && (
                <ErrorMessage>{errors.constraints.confirmationModalities}</ErrorMessage>
              )}
            </div>
          </Column>
        </Row>
        <Row>
          <Column>
            <DepartureDatesTitleWrapper>
              <Heading.H5>
                <FormattedMessage id="coupons.form.departureDates.label" defaultMessage="Departure dates" />
              </Heading.H5>

              <Button kind="secondary" type="button" onClick={addInterval}>
                <FormattedMessage id="coupons.form.departureDates.add" defaultMessage="Add" />
              </Button>
            </DepartureDatesTitleWrapper>
          </Column>
        </Row>
        {values.constraints.departureDateRanges.length === 0 && (
          <Row>
            <Column>
              <EmptyDepartureDates>
                <FormattedMessage id="coupons.form.departureDates.noInterval" defaultMessage="No interval selected." />
                &nbsp;
                <StyledLink as="span" onClick={addInterval}>
                  <FormattedMessage id="coupons.form.departureDates.addInterval" defaultMessage="Add interval" />
                </StyledLink>
              </EmptyDepartureDates>
            </Column>
          </Row>
        )}
        {values.constraints.departureDateRanges.map(({ from, to }: TSFixMe, index: TSFixMe): TSFixMe => {
          return (
            <Row key={index}>
              <Column>
                <NestedRow>
                  <Column>
                    <InputDate
                      id={`constraints.departureDateRanges[${index}].from`}
                      intl={intl}
                      value={from}
                      onChange={(value): TSFixMe => {
                        setFieldValue(`constraints.departureDateRanges[${index}].from`, value)
                        // @ts-expect-error ts(2322)
                        if (!to || isBefore(to, value)) {
                          setFieldValue(`constraints.departureDateRanges[${index}].to`, value)
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      touched={
                        touched.constraints &&
                        touched.constraints.departureDateRanges &&
                        touched.constraints.departureDateRanges[index] &&
                        touched.constraints.departureDateRanges[index].from
                      }
                      errors={
                        errors.constraints &&
                        errors.constraints.departureDateRanges &&
                        errors.constraints.departureDateRanges[index] &&
                        // @ts-expect-error ts(2322)
                        errors.constraints.departureDateRanges[index].from
                      }
                      placeholder={intl.formatMessage(messages.startsAtPlaceholder)}
                      label={
                        <FormattedMessage
                          id="coupons.form.interval.label"
                          defaultMessage="Interval {count}"
                          values={{
                            count: index + 1
                          }}
                        />
                      }
                    />
                  </Column>

                  <EndsAtColumn>
                    <InputDate
                      id={`constraints.departureDates[${index}].to`}
                      intl={intl}
                      value={to}
                      onChange={(value: TSFixMe): TSFixMe => {
                        setFieldValue(`constraints.departureDateRanges[${index}].to`, value)

                        if (!from || isBefore(value, from)) {
                          setFieldValue(`constraints.departureDateRanges[${index}].from`, value)
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      touched={
                        touched.constraints &&
                        touched.constraints.departureDateRanges &&
                        touched.constraints.departureDateRanges[index] &&
                        touched.constraints.departureDateRanges[index].to
                      }
                      errors={
                        errors.constraints &&
                        errors.constraints.departureDateRanges &&
                        errors.constraints.departureDateRanges[index] &&
                        // @ts-expect-error ts(2322) FIXME
                        errors.constraints.departureDateRanges[index].to
                      }
                      placeholder={intl.formatMessage(messages.endsAtPlaceholder)}
                      label=""
                    />

                    <IntervalDelete>
                      <div onClick={(): TSFixMe => deleteInterval(index)}>
                        <StyledCloseIcon />
                      </div>
                    </IntervalDelete>
                  </EndsAtColumn>
                </NestedRow>
              </Column>
            </Row>
          )
        })}
      </FormLayout.CardContent>

      <FormLayout.Split />

      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="coupons.form.error"
                defaultMessage="There was an error while creating your coupon"
              />
            </ErrorMessage>
          </div>
        )}

        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/coupons" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>

          <Button type="submit" kind="primary" loading={loading} disabled={!dirty}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewCoupon = ({ coupon, campaign, onSubmit, error, loading, isEdit }: INewCouponProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const getInitialCouponValue = ({ discountType, discountValue }: TSFixMe): TSFixMe => {
    if (discountType && discountValue) {
      if (discountType === DISCOUNT_TYPE.PERCENTAGE) {
        return discountValue
      } else {
        return sanitizePrice(discountValue / 100)
      }
    }

    return ''
  }
  const initialCoupon: INewCouponForm = {
    code: coupon?.code || getRandomCode(),
    company: coupon?.campaign ? coupon.campaign.company : getDefaultCompanyForForm(login.accessToken),
    /*@ts-expect-error ts(2322) FIXME*/
    campaign:
      coupon?.campaign && coupon.campaign.id
        ? coupon.campaign
        : {
            id: '',
            name: '',
            company: getDefaultCompanyForForm(login.accessToken),
            startsAt: '',
            endsAt: '',
            timezone: ''
          },
    startsAt: coupon?.startsAt ? new Date(coupon.startsAt) : '',
    endsAt: coupon?.endsAt ? new Date(coupon.endsAt) : '',
    channels: coupon?.channels || [],
    discountType: coupon?.discountType || (DISCOUNT_TYPE.PERCENTAGE as GTDiscountType),
    value: getInitialCouponValue({ discountType: coupon?.discountType, discountValue: coupon?.value }),
    cityOrigins: coupon?.constraints?.citySegments?.origins || [],
    cityDestinations: coupon?.constraints?.citySegments?.destinations || [],
    segments: coupon?.constraints?.citySegments?.segments || [],
    isReturn: coupon?.constraints?.citySegments?.isReturn || false,
    allSegments: coupon?.constraints?.citySegments?.isAllSegments || false,
    allCitiesAndSegments: coupon?.constraints?.citySegments?.allCitiesAndSegments || false,
    passengerTypes: coupon?.constraints?.passengerTypes || [],
    constraints: coupon?.constraints
      ? {
          salesChannels: coupon.constraints.salesChannelsV2 || [],
          departureDateRanges:
            coupon.constraints.departureDateRanges.map(({ from, to }: TSFixMe): TSFixMe => {
              return {
                from: new Date(`${from} 12:00`),
                to: new Date(`${to} 12:00`)
              }
            }) || [],
          segments: coupon?.constraints?.citySegments?.segments || [],
          minSale: coupon.constraints.minSale ? String(sanitizePrice(coupon.constraints.minSale / 100)) : '',
          limit: coupon.constraints.limit ? String(coupon.constraints.limit) : '',
          userLimit: coupon.constraints.userLimit ? String(coupon.constraints.userLimit) : USER_LIMIT.ONE_TIME,
          confirmationModalities: coupon.constraints.confirmationModalities || CONFIRMATION_MODALITIES_LIST,
          onlyRegisteredUsers: coupon.constraints.onlyRegisteredUsers || false,
          minTicketsCount:
            coupon.constraints.minTicketsCount !== undefined ? String(coupon.constraints.minTicketsCount) : '',
          bookingTypes: coupon.constraints.bookingTypes || []
        }
      : {
          segments: [],
          departureDateRanges: [],
          salesChannels: [],
          minSale: '',
          limit: '',
          userLimit: USER_LIMIT.ONE_TIME,
          confirmationModalities: CONFIRMATION_MODALITIES_LIST,
          onlyRegisteredUsers: false,
          minTicketsCount: '1',
          bookingTypes: BOOKING_TYPE_OPTIONS
        }
  }

  return (
    <Formik initialValues={initialCoupon} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(): ReactElement => {
        return <NewCouponForm isEdit={isEdit} error={error} loading={loading} campaign={campaign} />
      }}
    </Formik>
  )
}
